/**
 * @file useCompanyConfig.js
 * @description Custom hook for handling company code configuration
 */
import { useState, useEffect } from 'react';
import { getConfigData } from '../services/authService';
import { useAuthContext } from '../store/AuthContext';

/**
 * Custom hook that manages company code configuration and authentication data.
 *
 * @returns {Object} An object containing the following properties:
 *   - isCompanyCodeReady: A boolean indicating if the company code is ready for use.
 *   - authData: An object containing authentication data, including username, password, MFA methods, company code, and company configuration.
 *   - setAuthData: A function to update the authData state.
 */
export const useCompanyConfig = () => {
    const [isCompanyCodeReady, setIsCompanyCodeReady] = useState(false);
    const { setCompanyConfig, reloadCompanyTheme } = useAuthContext();
    const [authData, setAuthData] = useState({
        username: '',
        password: '',
        mfaMethods: '',
        companyCode: '',
        companyConfig: null,
    });

    /**
     * Decodes a potentially encoded company code.
     * Handles Base64 and URI encoding with improved heuristics to avoid false positives.
     *
     * @param {string} encodedCompanyCode - The potentially encoded company code.
     * @returns {string} The decoded company code.
     */
    const decodeCompanyCode = encodedCompanyCode => {
        if (!encodedCompanyCode) {
            return encodedCompanyCode;
        }

        try {
            const uriDecoded = decodeURIComponent(encodedCompanyCode);

            if (
                uriDecoded !== encodedCompanyCode &&
                !/[^\x20-\x7E]/.test(uriDecoded)
            ) {
                return uriDecoded;
            }
        } catch (error) {}

        try {
            const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
            const hasValidBase64Chars = base64Regex.test(encodedCompanyCode);
            const hasProperLength = encodedCompanyCode.length % 4 === 0;
            const hasBase64Padding =
                encodedCompanyCode.endsWith('=') ||
                encodedCompanyCode.endsWith('==');
            const isLongEnoughToBeIntentional = encodedCompanyCode.length >= 8;

            if (
                hasValidBase64Chars &&
                (hasProperLength ||
                    hasBase64Padding ||
                    isLongEnoughToBeIntentional)
            ) {
                const decodedCompanyCode = atob(encodedCompanyCode);

                if (
                    !/[^\x20-\x7E]/.test(decodedCompanyCode) &&
                    decodedCompanyCode.length > 0
                ) {
                    const isLikelyFalsePositive =
                        decodedCompanyCode.length <= 3 &&
                        /^[a-z]+$/.test(decodedCompanyCode) &&
                        encodedCompanyCode.length <= 4 &&
                        !hasBase64Padding;

                    if (!isLikelyFalsePositive) {
                        return decodedCompanyCode;
                    }
                }
            }
        } catch (error) {}

        return encodedCompanyCode;
    };

    useEffect(() => {
        /**
         * Initializes authentication data, including company code and configuration.
         *
         * @async
         */
        const initializeAuthData = async () => {
            // Only impersonation is cleared here; access_token must survive a
            // return to AuthPage (e.g. PWA relaunch) so a still-valid session isn't logged out.
            window.localStorage.removeItem('impersonation_token');
            window.localStorage.removeItem('tenantId');

            const queryParams = new URLSearchParams(window.location.search);
            let companyCode =
                queryParams.get('companyCode') ||
                queryParams.get('companycode') ||
                queryParams.get('clientcode');

            // Check if companyCode is base64 encoded
            companyCode = decodeCompanyCode(companyCode);

            const companyConfig = companyCode
                ? await getConfigData(companyCode)
                : null;

            if (companyCode) {
                setAppCompanyCode(companyCode);
            }
            await reloadCompanyTheme(companyCode || null);

            setAuthData({
                ...authData,
                companyCode: companyCode || '',
                companyConfig,
            });

            setCompanyConfig(companyConfig);

            setIsCompanyCodeReady(true);
        };

        initializeAuthData();
    }, []);

    /**
     * Sets the company code in the local storage to be use App wide
     * @param {string} companyCode - The company code to be set.
     * @returns {void}
     */
    const setAppCompanyCode = companyCode => {
        window.localStorage.setItem('companyCode', companyCode);
    };

    return { isCompanyCodeReady, authData, setAuthData };
};
